import { map } from 'ramda';
import { useMemo, useRef } from 'react';
import { DEFAULT_LIVE_CHAT_SECTION_COLORS } from '../../../constants/DefaultLiveChatSectionColors';
import type { Utils } from '../../UtilsProvider';
import { useUtils } from '../../UtilsProvider';

export type ProcessPlaceholders = (object: any) => any;

const translateText = (argsString: string, translate: Utils['translate']): string => {
  const [, key, values] = argsString.match(/^([^,]+)(?:,(.+))?$/) || [];
  try {
    return translate(key, JSON.parse(values));
  } catch {
    return translate(key);
  }
};

export interface ProcessPlaceholdersFactoryParams {
  locale: string;
  translate: Utils['translate'];
  getMediaUrl: Utils['media']['getMediaUrl'];
  getLiveChatSectionColors?: Utils['getLiveChatSectionColors'];
}

export const processPlaceholdersFactory = ({
  locale,
  translate,
  getMediaUrl,
  getLiveChatSectionColors = () => DEFAULT_LIVE_CHAT_SECTION_COLORS,
}: ProcessPlaceholdersFactoryParams): ProcessPlaceholders =>
  function _processPlaceholders(object): any {
    if (Array.isArray(object)) {
      return object.map(_processPlaceholders);
    }

    if (typeof object === 'object' && object !== null) {
      return map(_processPlaceholders, object);
    }

    if (typeof object === 'string') {
      return object.replace(/{!(\w+)(?:\(([^)]+)\)|\(\))?([^}]+)?}/g, (all, actionName, args, tail) => {
        if (tail) {
          throw new Error(`Error parsing placeholders. Incorrect placeholder: ${all}`);
        }

        switch (actionName) {
          case 'locale':
            return locale;

          case 'getMediaUrl':
            return getMediaUrl(args);

          case 'barColor':
            return getLiveChatSectionColors().barColor;

          case 'bubbleColor':
            return getLiveChatSectionColors().bubbleColor;

          case 'translate':
            return translateText(args, translate);

          case 'translateYoutubeChannel': {
            const youtubeUrl = translateText(args, translate);
            return youtubeUrl.split('/').pop() || '';
          }

          default:
            return all;
        }
      });
    }

    return object;
  };

export const useProcessPlaceholders = (params: ProcessPlaceholdersFactoryParams): ProcessPlaceholders => {
  const ref = useRef<ProcessPlaceholders>(processPlaceholdersFactory(params));
  return ref.current;
};

export const useProcessed = <T>(
  unprocessed: T,
  customProcessor?: (unprocessed: T, processPlaceholders: ProcessPlaceholders) => T,
): T => {
  const { processPlaceholders } = useUtils();

  return useMemo(() => {
    const processor = customProcessor || processPlaceholders;
    return processor(unprocessed, processPlaceholders);
  }, [unprocessed]); // eslint-disable-line react-hooks/exhaustive-deps
};
